import React from "react";
import { useNavigate } from "react-router-dom";
import { PDFViewer } from "@/components/document-writer/PDFViewer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { WORKFORCE_PERSONAS } from "../../../../../../convex/aiWorkforce/config";
import { getPersonaTheme } from "../personaTheme";
import {
  FileText,
  ExternalLink,
  Download,
  CheckCircle2,
  XCircle,
  Clock,
  Printer,
  Edit3,
} from "lucide-react";

interface DocumentDeliverableCanvasProps {
  artifact: {
    _id: string;
    title: string;
    type: string;
    createdPersona: string;
    status: string;
    data?: any;
    createdAt: number;
  };
  onApprove?: () => void;
  onReject?: () => void;
  isProcessing?: boolean;
}

export function DocumentDeliverableCanvas({
  artifact,
  onApprove,
  onReject,
  isProcessing = false,
}: DocumentDeliverableCanvasProps) {
  const navigate = useNavigate();
  const persona = WORKFORCE_PERSONAS[artifact.createdPersona] || WORKFORCE_PERSONAS.chief_of_staff;
  const theme = getPersonaTheme(persona.key);

  const pdfUrl = artifact.data?.pdfUrl || artifact.data?.fileUrl;
  const documentId = artifact.data?.documentId;
  const contentText = artifact.data?.content || artifact.data?.typstContent || artifact.data?.text;

  const handleOpenInWriter = () => {
    if (documentId) {
      navigate(`/document-writer`, { state: { documentId } });
    } else {
      navigate(`/document-writer`, { state: { fromArtifact: artifact } });
    }
  };

  const handleDownload = () => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank");
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50/50 rounded-2xl overflow-hidden border border-gray-200">
      {/* Document Header Bar */}
      <div className="p-4 bg-white border-b border-gray-200 flex items-center justify-between gap-4 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0 shadow-2xs">
            <FileText className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-bold text-gray-900 truncate">
                {artifact.title}
              </h3>
              <Badge variant="outline" className="text-xs uppercase font-semibold text-blue-700 bg-blue-50 border-blue-200">
                PDF Document
              </Badge>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Drafted by <span className="font-semibold text-gray-700">{persona.name}</span> • {new Date(artifact.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Action Toolbar */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {pdfUrl && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="gap-1.5 text-xs text-gray-700"
              title="Download PDF"
            >
              <Download className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Download</span>
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenInWriter}
            className="gap-1.5 text-xs text-blue-600 border-blue-200 bg-blue-50/50 hover:bg-blue-100 hover:text-blue-700 font-semibold shadow-2xs"
            title="Open in Document Writer Studio for line-by-line Typst edits"
          >
            <Edit3 className="w-3.5 h-3.5" />
            <span>Edit in Document Writer</span>
            <ExternalLink className="w-3.5 h-3.5 opacity-60" />
          </Button>
        </div>
      </div>

      {/* Main Document Content Area */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 flex flex-col items-center justify-start custom-scrollbar">
        {pdfUrl ? (
          <div className="w-full max-w-3xl bg-white shadow-md rounded-xl overflow-hidden border border-gray-200 min-h-[500px]">
            <PDFViewer pdfUrl={pdfUrl} className="w-full" />
          </div>
        ) : contentText ? (
          <div className="w-full max-w-3xl bg-white shadow-md rounded-xl p-8 border border-gray-200 prose prose-slate max-w-none">
            <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800 leading-relaxed">
              {contentText}
            </pre>
          </div>
        ) : (
          <div className="w-full max-w-md my-auto p-8 text-center bg-white border border-gray-200 rounded-2xl shadow-xs space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto">
              <FileText className="w-6 h-6" />
            </div>
            <h4 className="text-base font-bold text-gray-900">Document Ready for Compilation</h4>
            <p className="text-sm text-gray-500 leading-relaxed">
              This business artifact has been prepared by {persona.name}. You can preview and customize its sections in Document Writer.
            </p>
            <Button
              onClick={handleOpenInWriter}
              className="gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-sm"
            >
              <Edit3 className="w-4 h-4" />
              <span>Open in Document Writer</span>
            </Button>
          </div>
        )}
      </div>

      {/* Footer Approval Bar (if pending approval) */}
      {artifact.status === "pending_approval" && onApprove && (
        <div className="p-4 bg-white border-t border-gray-200 flex items-center justify-between gap-4 flex-shrink-0">
          <div className="flex items-center gap-2 text-xs font-semibold text-amber-800 bg-amber-50 px-3 py-1.5 rounded-full border border-amber-200">
            <Clock className="w-4 h-4 text-amber-600" />
            <span>Requires Executive Sign-Off before dispatch</span>
          </div>

          <div className="flex items-center gap-2.5">
            {onReject && (
              <Button
                variant="outline"
                size="sm"
                onClick={onReject}
                disabled={isProcessing}
                className="text-gray-700 gap-1.5 text-xs font-semibold"
              >
                <XCircle className="w-4 h-4 text-rose-500" />
                <span>Decline</span>
              </Button>
            )}
            <Button
              size="sm"
              onClick={onApprove}
              disabled={isProcessing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 text-xs font-semibold shadow-xs"
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>Approve Document</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
